/**
 * InfoCard - Tappable list item for Users, Deposits, Withdrawals
 *
 * Composed component: Uses searchUtils from core.
 *
 * Features:
 * - Direct tap to navigate to detail
 * - Up to 2 search highlights (one per search term)
 * - Pill-shaped, iOS-style shadow
 */

import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export type InfoCardType = 'user' | 'deposit' | 'withdrawal';

export interface InfoCardProps {
  /** Default label to display (@username, #REF-123, etc.) */
  label: string;
  /** Card type for styling variations */
  type: InfoCardType;
  /** Search highlights - up to 2 matched values to display */
  searchHighlights?: string[];
  /** Callback when card is tapped (navigate to detail) */
  onClick?: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Optional ID for accessibility */
  id?: string;
}

export function InfoCard({
  label,
  type: _type,
  searchHighlights = [],
  onClick,
  disabled = false,
  id,
}: InfoCardProps) {
  // _type reserved for future styling variations
  const handleClick = () => {
    if (disabled) return;
    onClick?.();
  };

  const hasHighlights = searchHighlights.length > 0;

  return (
    <div
      id={id}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        // Base styles
        'relative flex items-center justify-between w-full',
        'px-4 py-3.5 rounded-full',
        'bg-white',
        'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
        'border border-slate-200',
        'transition-all duration-200',
        'cursor-pointer',
        'select-none',

        // Hover/active states
        !disabled && 'hover:border-slate-300 active:bg-slate-50',

        // Disabled state
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      {/* Content area */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {hasHighlights ? (
          // Show highlights
          <>
            {searchHighlights.map((highlight, index) => (
              <span
                key={index}
                className={cn(
                  'text-sm font-medium truncate',
                  'text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md',
                  // Second highlight is slightly smaller
                  index === 1 && 'text-xs',
                )}
              >
                {highlight}
              </span>
            ))}
          </>
        ) : (
          // Show default label
          <span className="text-base font-medium text-slate-700 truncate">
            {label}
          </span>
        )}
      </div>

      {/* Chevron indicator */}
      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
    </div>
  );
}

/**
 * InfoCardList - Container for multiple InfoCards with consistent spacing
 */
export interface InfoCardListProps {
  children: React.ReactNode;
  /** Optional className for the container */
  className?: string;
}

export function InfoCardList({ children, className }: InfoCardListProps) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {children}
    </div>
  );
}

// Re-export search utilities for convenience
export {
  findSearchHighlights,
  cardMatchesSearch,
} from '../core/searchUtils';
